import { useEffect, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { HeroAmount } from "../components/motion";
import { IconReceipt } from "../components/icons";
import { Amount, Button, CopyButton, ExternalLink, Pill, Skeleton, StatusPill, type Tone } from "../components/ui";
import { useInvoice } from "../hooks/useInvoice";
import { useI18n } from "../i18n";
import { chainLabel, explorerAddressUrl, explorerTxUrl } from "../lib/chains";
import { formatDateTime, monogram } from "../lib/format";
import { formatUnits, shortHash } from "../lib/money";
import { isFinal, isSettled } from "../lib/status";
import type { PublicInvoice, PublicInvoiceRefund, RefundStatus } from "../lib/types";
import { ErrorState } from "./ErrorState";
import { PageShell } from "./PageShell";

/** /i/:id — the public answer to "did my payment arrive?", with on-chain evidence. */
export function StatusPage() {
  const { id = "" } = useParams();
  const { invoice, loading, error, reload } = useInvoice(id);
  const { t } = useI18n();

  useEffect(() => {
    document.title = `${t("receipt.title")} · ${invoice?.merchantName ?? ""}`;
  }, [invoice?.merchantName, t]);

  if (loading && !invoice) {
    return (
      <PageShell narrow>
        <main className="status-page" aria-busy="true">
          <div className="status-card panel">
            <Skeleton width={140} height={14} />
            <Skeleton width={240} height={40} radius={10} />
            <Skeleton height={120} radius={12} />
          </div>
        </main>
      </PageShell>
    );
  }
  if (!invoice) return <ErrorState code={error ?? "network"} onRetry={() => void reload()} />;
  return <StatusBody invoice={invoice} />;
}

function StatusBody({ invoice }: { invoice: PublicInvoice }) {
  const { t, locale } = useI18n();
  const chain = invoice.asset.chain;
  const dec = invoice.asset.decimals;
  const asset = invoice.asset.symbol;
  const tx = invoice.txHash;
  const txUrl = tx ? (invoice.explorerUrl ?? explorerTxUrl(chain, tx)) : null;
  const final = isFinal(invoice.status);
  const expiredPassed = Date.parse(invoice.expiresAt) <= Date.now();

  const timeline: Array<{ label: string; at: string | null; state: "done" | "todo" | "muted" }> = [
    { label: t("receipt.created"), at: invoice.createdAt, state: "done" },
  ];
  // The contract carries no separate detection timestamp, so this row has no time.
  if (tx && invoice.status === "confirming") timeline.push({ label: t("receipt.detected"), at: null, state: "done" });
  if (isSettled(invoice.status) || invoice.status === "underpaid" || invoice.status === "flagged") {
    timeline.push({ label: t("receipt.settled"), at: invoice.paidAt, state: "done" });
  } else if (invoice.status === "expired" || (!tx && expiredPassed)) {
    timeline.push({ label: t("receipt.expired"), at: invoice.expiresAt, state: "muted" });
  } else {
    timeline.push({ label: t("receipt.expires"), at: invoice.expiresAt, state: "todo" });
  }

  return (
    <PageShell narrow invoiceId={invoice.id} printTitle={t("print.title")}>
      <main className="status-page" id="main">
        <div className="status-card panel">
          <div className="status-top">
            <div className="merchant">
              <span className="monogram" aria-hidden="true">
                {monogram(invoice.merchantName)}
              </span>
              <span className="merchant-name">{invoice.merchantName}</span>
            </div>
            <div aria-live="polite">
              <StatusPill status={invoice.status} size="lg" />
            </div>
          </div>

          <div className="status-amount">
            <div className="due-label">{final ? t("pay.total") : t("receipt.amountDue")}</div>
            <HeroAmount value={formatUnits(invoice.amount, dec)} symbol={asset} animate={false} />
            {invoice.description ? <p className="due-description">{invoice.description}</p> : null}
          </div>

          <p className="muted small">{t("receipt.subtitle", { merchant: invoice.merchantName })}</p>

          <section className="status-section">
            <h2 className="field-label">{t("receipt.timeline")}</h2>
            <ol className="timeline">
              {timeline.map((row) => (
                <li key={row.label} className={`timeline-row is-${row.state}`}>
                  <span className="timeline-dot" aria-hidden="true" />
                  <span className="timeline-label">{row.label}</span>
                  <span className="timeline-at num">{row.at ? formatDateTime(row.at, locale) : null}</span>
                </li>
              ))}
            </ol>
          </section>

          <section className="status-section">
            <h2 className="field-label">{t("receipt.evidence")}</h2>
            {!tx && !final ? <p className="muted small">{t("receipt.noTx")}</p> : null}
            <dl className="evidence">
              {tx && txUrl ? (
                <Evidence label={t("receipt.txHash")} value={tx} href={txUrl} />
              ) : null}
              {invoice.receivedAmount ? (
                <EvidenceRow label={t("receipt.received")}>
                  <Amount value={formatUnits(invoice.receivedAmount, dec)} symbol={asset} />
                </EvidenceRow>
              ) : null}
              <EvidenceRow label={t("pay.network")}>{chainLabel(chain)}</EvidenceRow>
              {invoice.payerAddress ? (
                <Evidence label={t("receipt.payer")} value={invoice.payerAddress} href={explorerAddressUrl(chain, invoice.payerAddress)} />
              ) : null}
              <Evidence label={t("receipt.merchant")} value={invoice.merchantAddress} href={explorerAddressUrl(chain, invoice.merchantAddress)} />
              <Evidence label={t("receipt.token")} value={invoice.asset.address} href={explorerAddressUrl(chain, invoice.asset.address)} suffix={asset} />
              <Evidence label={t("receipt.reference")} value={invoice.reference} />
              <EvidenceRow label={t("common.invoice")}>
                <span className="mono small">{invoice.id}</span>
              </EvidenceRow>
            </dl>
            {tx ? <p className="muted small">{t("receipt.verify")}</p> : null}
          </section>

          {invoice.refunds && invoice.refunds.length > 0 ? <Refunds refunds={invoice.refunds} invoice={invoice} /> : null}

          {!final ? (
            <Link className="btn btn-primary btn-lg btn-block" to={`/pay/${encodeURIComponent(invoice.id)}`}>
              <span className="btn-label">{t("receipt.openCheckout")}</span>
            </Link>
          ) : (
            <div className="no-print">
              <Button variant="secondary" block onClick={() => window.print()}>
                <IconReceipt size={16} />
                {t("done.download")}
              </Button>
              <p className="muted small center status-download-hint">{t("done.downloadHint")}</p>
            </div>
          )}
        </div>
      </main>
    </PageShell>
  );
}

const REFUND_TONE: Record<RefundStatus, Tone> = {
  requested: "neutral",
  awaiting_customer: "warning",
  ready: "info",
  submitted: "info",
  confirmed: "success",
  failed: "danger",
  cancelled: "neutral",
};

export function RefundPill({ status }: { status: RefundStatus }) {
  const { t } = useI18n();
  return <Pill tone={REFUND_TONE[status]} label={t(`refundStatus.${status}`)} live={status === "submitted"} />;
}

function Refunds({ refunds, invoice }: { refunds: PublicInvoiceRefund[]; invoice: PublicInvoice }) {
  const { t, locale } = useI18n();
  const dec = invoice.asset.decimals;
  const asset = invoice.asset.symbol;
  const confirmed = refunds.filter((r) => r.status === "confirmed").reduce((sum, r) => sum + BigInt(r.amount), 0n);
  return (
    <section className="status-section" aria-labelledby="refunds-title">
      <div className="section-head">
        <h2 className="field-label" id="refunds-title">
          {t("receipt.refunds")}
        </h2>
        {confirmed > 0n ? (
          <span className="muted small num">{t("receipt.refundedTotal", { amount: formatUnits(confirmed.toString(), dec), asset })}</span>
        ) : null}
      </div>
      <ul className="refund-list" role="list">
        {refunds.map((r) => (
          <li key={r.id} className="refund-row">
            <div className="refund-main">
              <Amount value={formatUnits(r.amount, dec)} symbol={asset} className="strong" />
              <RefundPill status={r.status} />
            </div>
            <div className="refund-meta muted small">
              <span className="num">
                {r.confirmedAt
                  ? t("receipt.refundConfirmed", { date: formatDateTime(r.confirmedAt, locale) })
                  : t("receipt.refundRequested", { date: formatDateTime(r.createdAt, locale) })}
              </span>
              {r.txHash && r.explorerUrl ? (
                <ExternalLink href={r.explorerUrl}>
                  <span className="mono">{shortHash(r.txHash, 6, 6)}</span>
                </ExternalLink>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function EvidenceRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="evidence-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Evidence({ label, value, href, suffix }: { label: string; value: string; href?: string; suffix?: string }) {
  return (
    <EvidenceRow label={label}>
      <span className="evidence-value">
        {href ? (
          <ExternalLink href={href}>
            <span className="mono">{shortHash(value, 8, 8)}</span>
          </ExternalLink>
        ) : (
          <span className="mono">{shortHash(value, 8, 8)}</span>
        )}
        {suffix ? <span className="muted small">{suffix}</span> : null}
        <CopyButton value={value} label={label} compact />
      </span>
    </EvidenceRow>
  );
}
